/**
 * Client-only text transforms a message body needs at render/copy time:
 * mention markup encode/parse/strip, and the long-message fold threshold.
 *
 * A mention is encoded inline in `body` as `@[DisplayName](userId)`, composed
 * by the composer when a fan picks a suggestion, parsed back out at render
 * time. No structured mention field exists on the message model, and none is
 * requested (ISSUE-280 plan §5.8).
 */

const MENTION_TOKEN_RE = /@\[([^\]]+)\]\(([^)]+)\)/g;

export type typeMentionRun =
  | { kind: "text"; value: string }
  | { kind: "mention"; displayName: string; userId: string };

/** Splits body on the markup token, in order. A malformed or truncated token
 *  (a `@[` with no closing `)`) renders as plain text, never throws. */
export function parseMentionRuns(body: string): typeMentionRun[] {
  const runs: typeMentionRun[] = [];
  let lastIndex = 0;
  MENTION_TOKEN_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = MENTION_TOKEN_RE.exec(body)) !== null) {
    if (match.index > lastIndex) {
      runs.push({ kind: "text", value: body.slice(lastIndex, match.index) });
    }
    runs.push({
      kind: "mention",
      displayName: match[1] ?? "",
      userId: match[2] ?? "",
    });
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < body.length) {
    runs.push({ kind: "text", value: body.slice(lastIndex) });
  }

  if (runs.length === 0) {
    return [{ kind: "text", value: body }];
  }

  return runs;
}

/** The exact token the composer inserts when a fan picks a suggestion. */
export function buildMentionToken(displayName: string, userId: string): string {
  return `@[${displayName}](${userId})`;
}

/** Human-readable text for Copy: every mention token collapses to "@DisplayName". */
export function stripMentionMarkup(body: string): string {
  return body.replace(
    MENTION_TOKEN_RE,
    (_match, displayName: string) => `@${displayName}`,
  );
}

export const LONG_MESSAGE_FOLD_CHARS = 600;

export function foldMessageBody(
  body: string,
  isExpanded: boolean,
): { visibleText: string; isFoldable: boolean } {
  const codePoints = Array.from(body);
  const isFoldable = codePoints.length > LONG_MESSAGE_FOLD_CHARS;

  if (!isFoldable) {
    return { visibleText: body, isFoldable: false };
  }

  if (isExpanded) {
    return { visibleText: body, isFoldable: true };
  }

  return {
    visibleText: `${codePoints.slice(0, LONG_MESSAGE_FOLD_CHARS).join("")}…`,
    isFoldable: true,
  };
}

/**
 * ISSUE-1359. A web address inside a text run, split out so the bubble can
 * draw it as a real link. Only `http://` and `https://` addresses count: a
 * `javascript:` or `data:` string never becomes a link, and a bare
 * `example.com` stays plain text the way v1 drew it.
 */
export type typeLinkRun =
  | { kind: "text"; value: string }
  | { kind: "link"; value: string; href: string };

const LINK_CANDIDATE_RE = /https?:\/\/[^\s<>"]+/gi;

/** Punctuation a sentence puts right after an address, never part of it. */
const TRAILING_PUNCTUATION_RE = /[.,;:!?'"*]+$/;

function trimLinkCandidate(candidate: string): string {
  let trimmed = candidate.replace(TRAILING_PUNCTUATION_RE, "");
  // A closing parenthesis belongs to the address only when the address
  // opened one itself, as in a Wikipedia link. "(see https://a.com)" keeps
  // its own parenthesis outside the link.
  while (trimmed.endsWith(")")) {
    const opens = trimmed.split("(").length - 1;
    const closes = trimmed.split(")").length - 1;
    if (closes <= opens) break;
    trimmed = trimmed.slice(0, -1).replace(TRAILING_PUNCTUATION_RE, "");
  }
  return trimmed;
}

function toSafeHref(candidate: string): string | null {
  try {
    const url = new URL(candidate);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    if (url.hostname === "") return null;
    return url.href;
  } catch {
    return null;
  }
}

/** Splits plain text into text and link runs, in order. Never throws. */
export function parseLinkRuns(text: string): typeLinkRun[] {
  const runs: typeLinkRun[] = [];
  let pending = "";
  let lastIndex = 0;
  LINK_CANDIDATE_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = LINK_CANDIDATE_RE.exec(text)) !== null) {
    const candidate = trimLinkCandidate(match[0]);
    const href = candidate === "" ? null : toSafeHref(candidate);
    pending += text.slice(lastIndex, match.index);
    if (href === null) {
      pending += match[0];
      lastIndex = match.index + match[0].length;
      continue;
    }
    if (pending !== "") {
      runs.push({ kind: "text", value: pending });
      pending = "";
    }
    runs.push({ kind: "link", value: candidate, href });
    lastIndex = match.index + candidate.length;
    LINK_CANDIDATE_RE.lastIndex = lastIndex;
  }

  pending += text.slice(lastIndex);
  if (pending !== "") runs.push({ kind: "text", value: pending });
  return runs.length === 0 ? [{ kind: "text", value: text }] : runs;
}
